import { useState } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { usePermissions } from '@/hooks/use-permissions';
import { SettingsPage, SettingsTableHead, useTabParam, type SettingsTab } from '@/modules/settings';
import { EscalationLog, EscalationLogTools } from '@/modules/escalation-policy/escalation-log';
import { ESCALATION_LOG_COLUMNS } from '@/modules/escalation-policy/escalation-log-columns';
import { useEscalationLogFilters } from '@/modules/escalation-policy/use-escalation-log-filters';
import { AutomationLogsTools, SentRepliesTab } from './automation-sent-replies';
import { AUTOMATION_LOG_COLUMNS, useAutomationLogFilters } from './automation-log-filters';
import { SettingsLog, SettingsLogTools } from './settings-log';
import { SETTINGS_LOG_COLUMNS } from './settings-log-columns';
import type { DayRange } from '@/lib/automation-replies';

type LogsTab = 'automation' | 'escalations' | 'settings';

const AUTOMATION_TAB: SettingsTab<LogsTab> = { value: 'automation', label: 'Automation' };
const ESCALATIONS_TAB: SettingsTab<LogsTab> = { value: 'escalations', label: 'Escalations' };
const SETTINGS_TAB: SettingsTab<LogsTab> = { value: 'settings', label: 'Settings' };

/** Settings › Logs: what Kim did on its own, every escalation it ran, and who changed the settings. `?tab=` opens a tab directly. */
export default function LogsPage() {
	const { can } = usePermissions();
	// Escalations and settings changes are brand settings; the page itself is gated on automation.
	const tabs = can('view', 'brand') ? [AUTOMATION_TAB, ESCALATIONS_TAB, SETTINGS_TAB] : [AUTOMATION_TAB];
	const [tab, setTab] = useTabParam(tabs, 'automation');
	const automationFilters = useAutomationLogFilters();
	const escalationFilters = useEscalationLogFilters();
	const [settingsDays, setSettingsDays] = useState<DayRange>({});
	const header = {
		automation: { tools: <AutomationLogsTools filters={automationFilters} />, columns: AUTOMATION_LOG_COLUMNS },
		escalations: { tools: <EscalationLogTools filters={escalationFilters} />, columns: ESCALATION_LOG_COLUMNS },
		settings: { tools: <SettingsLogTools days={settingsDays} onDaysChange={setSettingsDays} />, columns: SETTINGS_LOG_COLUMNS }
	}[tab];

	return (
		<SettingsPage
			title='Logs'
			tabs={tabs}
			tab={tab}
			onTabChange={setTab}
			tools={header.tools}
			subHeader={<SettingsTableHead columns={header.columns} />}
		>
			<TabsContent value='automation' className='mt-0'>
				<SentRepliesTab filters={automationFilters} />
			</TabsContent>
			<TabsContent value='escalations' className='mt-0'>
				<EscalationLog filters={escalationFilters} />
			</TabsContent>
			<TabsContent value='settings' className='mt-0'>
				<SettingsLog days={settingsDays} />
			</TabsContent>
		</SettingsPage>
	);
}
